import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import DoorCta from './DoorCta';
import useDoorPage, { studioHrefFor } from '../hooks/useDoorPage';
import { DOOR_GOPRO_PHOTO_MODE } from '../doorAttribution';
import { RETENTION_DAYS } from '../photoUpload/caps';
import { PRICING_TIERS, FREE_SIGNUP_CREDITS } from '../pricing/plans';
import { displayNameOf } from '../lib/operations';
import { FPS_OPTIONS, DEFAULT_FPS } from './project/photoCompileOptions';
import './DoorPage.css';

// GoPro spoke (#388). Device tail for the photo-sequence hub: "GoPro time lapse
// photos to video", "convert GoPro timelapse photos". Time Lapse PHOTO mode
// (as opposed to Time Lapse Video) saves one still per interval and GoPro's own
// guidance is that you stitch them yourself — so the visitor arrives holding a
// folder of JPGs and an unfinished job.
//
// Numbers come from caps.js / plans.js and the operation name from the registry
// (#379/#400), never retyped. Content only: no upload surface on this page.

const FREE_TIER = PRICING_TIERS.find((tier) => tier.id === 'free');
const SOLO_TIER = PRICING_TIERS.find((tier) => tier.id === 'solo_founding');

const FREE_POOL_LABEL = `${FREE_TIER.storageGb} GB`;
const FIX_FLICKER = displayNameOf('deflicker');

const FPS_CHOICES =
  `${FPS_OPTIONS.map(String).join(', ').replace(/, (\d+)$/, ', or $1')} fps`;
const DEFAULT_FPS_LABEL = `${DEFAULT_FPS} fps`;

const SECTION_IDS = [
  'door_hero',
  'door_what',
  'door_how',
  'door_flicker',
  'door_free',
  'door_faq',
  'door_final_cta',
];

const TOC = [
  { id: 'door_what', label: 'What Time Lapse Photo mode leaves you' },
  { id: 'door_how', label: 'Turning the folder into a video' },
  { id: 'door_flicker', label: 'If the result flickers' },
  { id: 'door_free', label: 'What this costs' },
  { id: 'door_faq', label: 'FAQ' },
];

const STEPS = [
  {
    name: 'Create a free account and start a project',
    text: 'No credit card and nothing to install. One project per shoot: it holds the stills, the compiled video, and every edit you render from it.',
  },
  {
    name: 'Upload the photos on the Footage tab',
    text: "Drop the folders straight off the card — 100GOPRO, 101GOPRO and the rest, all at once. Frames are ordered by EXIF capture time, so a copy that scrambled the order or arrived in several drops still rebuilds in shooting order, and anything already uploaded is skipped instead of duplicated.",
  },
  {
    name: 'Pick a frame rate and compile',
    text: `${FPS_CHOICES}, ${DEFAULT_FPS_LABEL} by default — the estimated length of the finished video is shown next to the control before you commit. The compile runs on our workers and writes the video back into the project as its source footage.`,
  },
  {
    name: 'Edit on the Edits tab',
    text: `Optional but usually worth it: trim the stillness, and switch on ${FIX_FLICKER} if the sequence strobes. Flicker is measured automatically, so the card tells you whether yours does.`,
  },
  {
    name: 'Render and download',
    text: 'The finished video is yours to download. Renders on the free plan carry a Tweedia watermark.',
  },
];

const FAQ = [
  {
    q: 'How do I turn GoPro time lapse photos into a video?',
    a: `Create a free Tweedia Studio account, start a project, and drop the photo folders onto the Footage tab. Pick a frame rate (${FPS_CHOICES}) and compile — the sequence becomes a video inside the project, which you then edit and render. Nothing is installed; it happens in the browser.`,
  },
  {
    q: "What is the difference between Time Lapse Video and Time Lapse Photo?",
    a: 'Time Lapse Video makes the movie in-camera and leaves you a finished .mp4 — upload that directly and skip the compile step. Time Lapse Photo saves each frame as its own still, which gives you the full-resolution originals and the freedom to choose the frame rate afterwards, at the cost of having to stitch them yourself.',
  },
  {
    q: 'How many photos can I upload?',
    a: `There is no per-project frame limit. The one ceiling is your account's pooled storage allowance, shared across every project — ${FREE_POOL_LABEL} on the free plan — and the uploader adds your batch to what you already store and tells you before a single file moves if it will not fit.`,
  },
  {
    q: 'Do I need to rename or renumber the files first?',
    a: 'No. Ordering comes from the EXIF capture time each frame carries; when that is missing, the file date is used, with the filename as the tiebreak. GoPro writes capture time on every still, so the sequence reconstructs itself.',
  },
  {
    q: 'What frame rate should I choose?',
    a: `${FPS_CHOICES}. A higher rate plays smoother and makes a shorter video from the same number of photos — 1,800 frames is 75 seconds at ${DEFAULT_FPS_LABEL} and 30 seconds at 60 fps. The estimate is shown before you compile, and you can rebuild at a different rate later without re-uploading.`,
  },
  {
    q: 'Is it free?',
    a: `The account is free and includes ${FREE_TIER.storageGb} GB of pooled storage and ${FREE_SIGNUP_CREDITS} starter credits, which is what pays for compiling and rendering. Free renders carry a Tweedia watermark, and raw photos are cleared ${RETENTION_DAYS} days after a project's last activity. ${SOLO_TIER.name}, at ${SOLO_TIER.priceLabel}${SOLO_TIER.priceSuffix}, removes the watermark.`,
  },
];

function buildJsonLd() {
  const url = 'https://tweediastudio.com/guides/gopro-time-lapse-photos-to-video';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        name: 'GoPro Time Lapse Photos to Video',
        description:
          'Turn the folder of JPGs from GoPro Time Lapse Photo mode into a timelapse video in your browser.',
        url,
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
      },
      {
        '@type': 'HowTo',
        '@id': `${url}#howto`,
        name: 'How to turn GoPro Time Lapse photos into a video',
        description:
          'Upload the GoPro photo folders to a Tweedia Studio project, compile them at your chosen frame rate, then edit and render.',
        step: STEPS.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: step.name,
          url: `${url}#door_how`,
          text: step.text,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

const STUDIO_HREF = studioHrefFor(DOOR_GOPRO_PHOTO_MODE);

function GoproPhotoModeGuidePage({ user, signIn }) {
  const signedIn = !!user;

  const { studioHref, setSectionRef, onCtaActivate, onNavClick } = useDoorPage({
    door: DOOR_GOPRO_PHOTO_MODE,
    signedIn,
    signIn,
    sectionIds: SECTION_IDS,
    meta: {
      title: 'GoPro Time Lapse Photos to Video — Free | Tweedia Studio',
      description:
        'GoPro Time Lapse Photo mode leaves a folder of JPGs. Compile them into a timelapse video in your browser, fix flicker, and render — free account.',
      canonicalPath: '/guides/gopro-time-lapse-photos-to-video',
      jsonLd: buildJsonLd(),
    },
  });

  const renderCta = (ctaId, label, testId) => (
    <DoorCta
      ctaId={ctaId}
      testId={testId}
      signedIn={signedIn}
      studioHref={studioHref}
      onActivate={onCtaActivate}
    >
      {label}
    </DoorCta>
  );

  return (
    <div className="door-page">
      <Header user={user} signIn={signIn} />
      <main className="door-content" data-testid="door-content">
        <section
          className="door-hero"
          id="door_hero"
          data-section-id="door_hero"
          ref={setSectionRef('door_hero')}
        >
          <p className="door-eyebrow">Guide · GoPro</p>
          <h1>GoPro Time Lapse photos to video</h1>
          <p className="door-lede">
            Time Lapse Photo mode does exactly what it says: it saves a photograph per interval
            and stops there. Tweedia Studio takes that folder, rebuilds it in shooting order, and
            compiles it into a video you can edit and download — in the browser, on a free
            account.
          </p>
          <div className="door-cta-row">
            {renderCta('door_gopro_hero', 'Start free — upload your photos', 'door-cta-hero')}
          </div>
          <p className="door-note">
            Free account, no credit card. Renders on the free plan carry a Tweedia watermark.
          </p>
        </section>

        <nav className="door-toc" aria-label="On this page">
          <p className="door-toc-title">On this page</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="door_what" data-section-id="door_what" ref={setSectionRef('door_what')}>
          <h2>What Time Lapse Photo mode leaves you</h2>
          <p>
            One JPG per interval, written into GoPro's numbered <code>100GOPRO</code>-style
            folders, and nothing else. A half-hour run at two seconds is 900 files; an all-day
            shoot is several thousand, often split across folders the camera rotated through on
            its own.
          </p>
          <p>
            GoPro says as much itself: in Time Lapse <strong>Photo</strong> mode the camera{' '}
            <em>
              saves each still image separately and you can stitch them together yourself with
              your preferred software
            </em>{' '}
            — with no recommendation for what that software should be. The frames are correct.
            The job is unfinished.
          </p>
          <p>
            That is a fair trade rather than an oversight: stills keep the full-resolution
            originals and leave the frame rate to you, which Time Lapse <strong>Video</strong>{' '}
            mode bakes in at capture. If your card holds an .mp4 instead, upload that directly
            and skip straight to editing.
          </p>
        </section>

        <section id="door_how" data-section-id="door_how" ref={setSectionRef('door_how')}>
          <h2>Turning the folder into a video</h2>
          <ol className="door-steps" data-testid="door-steps">
            {STEPS.map((step) => (
              <li key={step.name}>
                <strong>{step.name}.</strong> {step.text}
              </li>
            ))}
          </ol>
          <div className="door-crosslinks" data-testid="door-crosslinks">
            <Link
              to="/photo-sequence-to-video"
              className="door-crosslink"
              data-testid="door-link-photo-sequence"
              onClick={onNavClick('photo_sequence_door_from_gopro')}
            >
              <span className="door-crosslink-title">Photo sequence to video →</span>
              <span className="door-crosslink-text">
                The general version: any camera's stills, ordered by capture time.
              </span>
            </Link>
            <Link
              to="/deflicker-timelapse"
              className="door-crosslink"
              data-testid="door-link-deflicker"
              onClick={onNavClick('deflicker_door_from_gopro')}
            >
              <span className="door-crosslink-title">Deflicker a timelapse →</span>
              <span className="door-crosslink-text">
                What causes the strobe in a photo sequence, and how to smooth it.
              </span>
            </Link>
          </div>
        </section>

        <section
          id="door_flicker"
          data-section-id="door_flicker"
          ref={setSectionRef('door_flicker')}
        >
          <h2>If the result flickers</h2>
          <p>
            Photo sequences strobe more often than in-camera timelapse videos do, and the reason
            is structural: every still was metered on its own. Outdoors over a long run, passing
            cloud and moving shadow shift the exposure between consecutive frames, and at{' '}
            {DEFAULT_FPS_LABEL} those shifts land back to back.
          </p>
          <p>
            Once the compile finishes, the footage is analyzed and its frame-to-frame brightness
            measured. When flicker is found the card on the Edits tab flags it, and you switch on{' '}
            {FIX_FLICKER} — your choice sticks from then on. It smooths the jitter between frames
            without flattening the slow change in light across the shoot.
          </p>
        </section>

        <section id="door_free" data-section-id="door_free" ref={setSectionRef('door_free')}>
          <h2>What this costs</h2>
          <ul className="door-free-list" data-testid="door-free-list">
            <li>
              <strong>{FREE_POOL_LABEL} of pooled storage</strong> across unlimited projects.
              Raw stills, the compiled video and your renders all count against it.
            </li>
            <li>
              <strong>No per-project frame limit</strong> — the pooled allowance is the only
              ceiling, on every plan.
            </li>
            <li>
              <strong>{FREE_SIGNUP_CREDITS} starter credits</strong>. Credits pay for compiling
              and rendering; a longer or higher-resolution job uses more of them.
            </li>
            <li>
              <strong>Renders carry a Tweedia watermark</strong> on the free plan. Every plan
              exports up to 4K.
            </li>
            <li>
              <strong>Raw photos are kept {RETENTION_DAYS} days</strong> after a project's last
              activity on the free plan, then cleared. Your latest render of an edit stays
              available — only the raw stills age out.
            </li>
          </ul>
          <p>
            {SOLO_TIER.name} at {SOLO_TIER.priceLabel}
            {SOLO_TIER.priceSuffix} removes the watermark, raises the pool to{' '}
            {SOLO_TIER.storageGb} GB and keeps your raw photos while the subscription is active.{' '}
            <Link to="/pricing" onClick={onNavClick('pricing_from_gopro_door')}>
              See the plans
            </Link>
            .
          </p>
        </section>

        <section id="door_faq" data-section-id="door_faq" ref={setSectionRef('door_faq')}>
          <h2>FAQ</h2>
          <div className="door-faq" data-testid="door-faq">
            {FAQ.map((item) => (
              <div className="door-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="door-final-cta"
          id="door_final_cta"
          data-section-id="door_final_cta"
          ref={setSectionRef('door_final_cta')}
        >
          <h2>Bring the folder off the card</h2>
          <p>
            Drop the stills, pick a frame rate, and let the sequence become a video you can edit
            and download in the same place your next shoot will live.
          </p>
          <div className="door-cta-row">
            {renderCta('door_gopro_final', 'Create your free account', 'door-cta-final')}
            <Link
              to="/photo-sequence-to-video"
              className="door-secondary-cta"
              data-testid="door-secondary-photo-sequence"
              onClick={onNavClick('photo_sequence_door_from_gopro_final')}
            >
              Photo sequence to video
            </Link>
          </div>
          <p className="door-note">
            Free renders carry a Tweedia watermark. No credit card required to start.
          </p>
        </section>

        <p className="door-footnote" data-testid="door-footnote">
          GoPro is a trademark of GoPro, Inc. Tweedia Studio is not affiliated with, endorsed by
          or sponsored by GoPro. Mode names and behaviour vary across camera models and firmware
          — check your own camera's documentation.
        </p>
      </main>
      <Footer />
    </div>
  );
}

export default GoproPhotoModeGuidePage;
export { FAQ, STEPS, SECTION_IDS, STUDIO_HREF };
